import { useEffect, useState } from 'react';

/**
 * Custom hook that debounces a value.
 *
 * @template T - The type of the value to debounce.
 * @param {T} value - The value to debounce.
 * @param {number} [delay=500] - The debounce delay in milliseconds.
 * @returns {T} - The debounced value.
 *
 * @example
 * const debouncedSearchTerm = useDebounce<string>(searchTerm, 300);
 */
export function useDebounce<T>(value: T, delay: number = 500): T {
  // State to store the debounced value
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  /**
   * Function to handle the debounce logic.
   * Sets a timeout to update the debounced value after the specified delay.
   * Clears the timeout if the value or delay changes before the timeout completes.
   */
  function handleDebounce() {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }

  // useEffect hook to apply the debounce logic whenever the value or delay changes
  useEffect(handleDebounce, [value, delay]);

  // Return the debounced value
  return debouncedValue;
}
